import { useState } from 'react';

import {
  audienceLine,
  budgetLine,
  journeysOn,
  smsOffFor,
} from './journeySummary';
import { Notice } from './styles';

import Button from '@/components/Button';
import Modal from '@/components/Modal';
import { JourneysPayload } from '@/services/lifecycleAPI';
import styled from '@/styles';

const Content = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '18px 24px',
});

const Actions = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  padding: '14px 24px',
  borderTop: '1px solid #EDEDED',
});

type Props = {
  data: JourneysPayload;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
};

const GoLiveModal = ({ data, saving, error, onCancel, onConfirm }: Props) => {
  const [checked, setChecked] = useState(false);
  const { global, journeys } = data;
  const smsOff = smsOffFor(journeys);

  return (
    <Modal
      disableClickout
      size="sm"
      title="Start sending messages?"
      onRequestClose={saving ? undefined : onCancel}
    >
      <div data-testid="goLiveModal">
        <Content>
          <span>In live mode, with the settings saved now:</span>
          <ul
            style={{
              margin: 0,
              paddingLeft: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <li>{audienceLine(global)}</li>
            <li>Journeys switched on: {journeysOn(journeys)}.</li>
            {smsOff.length > 0 && <li>SMS off for: {smsOff.join(', ')}.</li>}
            <li>{budgetLine(global)}</li>
          </ul>
          {global.heartbeat_stale && (
            <Notice tone="amber">
              The engine has not run in the last 2 hours. Messages will not go
              out until it runs.
            </Notice>
          )}
          {!global.kill_switch_on && (
            <Notice tone="red">
              The kill switch is off, so nothing is sent even in live mode. The
              mode is still saved.
            </Notice>
          )}
          <label
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              paddingTop: 10,
            }}
          >
            <input
              checked={checked}
              data-testid="goLiveConfirmCheck"
              style={{ width: 18, height: 18 }}
              type="checkbox"
              onChange={(e) => setChecked(e.target.checked)}
            />
            <strong>I checked the list above</strong>
          </label>
          {error && (
            <div role="alert">
              <Notice tone="red">{error}</Notice>
            </div>
          )}
        </Content>
        <Actions>
          <Button
            a11yLabel="Cancel"
            appearance="outline"
            disabled={saving}
            label="Cancel"
            onClick={onCancel}
          />
          <Button
            a11yLabel="Start sending"
            appearance="primary"
            data-testid="goLiveConfirmBtn"
            disabled={!checked}
            isLoading={saving}
            label="Start sending"
            onClick={onConfirm}
          />
        </Actions>
      </div>
    </Modal>
  );
};

export default GoLiveModal;
